import React from 'react';
import CustomSelect from '../ui/CustomSelect';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    onPageChange: (page: number) => void;
    onItemsPerPageChange: (itemsPerPage: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    hasNextPage,
    hasPreviousPage,
    onPageChange,
    onItemsPerPageChange
}) => {
    if (totalPages <= 1) {
        return null;
    }

    // Calculate visible page numbers
    const getVisiblePages = () => {
        const pages: (number | string)[] = [];
        const maxVisible = 5;

        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= 4; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 3; i <= totalPages; i++) {
                    pages.push(i);
                }
            } else {
                pages.push(1);
                pages.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            }
        }

        return pages;
    };

    const visiblePages = getVisiblePages();

    return (
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 px-4 w-full">
            {/* Items per page selector */}
            <div className="flex items-center gap-2 relative" style={{ zIndex: 9999 }}>
                <span className="text-sm text-text-description">Show:</span>
                <CustomSelect
                    value={itemsPerPage.toString()}
                    onChange={(value) => onItemsPerPageChange(Number(value))}
                    options={[5, 10, 20, 50].map((size) => ({
                        value: size.toString(),
                        label: size.toString()
                    }))}
                    className="min-w-[50px] w-[50px]"
                    openUpward={true}
                    showChevron={false}
                />
            </div>

            {/* Page navigation */}
            <div className="flex items-center gap-2">
                <button
                    className={`px-3 py-1 rounded-md text-sm ${hasPreviousPage
                        ? 'bg-secondary-500 text-white hover:bg-secondary-600'
                        : 'bg-accent-sand text-text-description cursor-not-allowed'
                        }`}
                    onClick={() => hasPreviousPage && onPageChange(currentPage - 1)}
                    disabled={!hasPreviousPage}
                >
                    Previous
                </button>

                <div className="flex items-center gap-1">
                    {visiblePages.map((page, index) => (
                        <React.Fragment key={index}>
                            {page === '...' ? (
                                <span className="px-2 text-text-description">...</span>
                            ) : (
                                <button
                                    className={`w-8 h-8 rounded-md text-sm ${currentPage === page
                                        ? 'bg-secondary-500 text-white'
                                        : 'bg-accent-sand text-text-description hover:bg-accent-taupe'
                                        }`}
                                    onClick={() => onPageChange(page as number)}
                                >
                                    {page}
                                </button>
                            )}
                        </React.Fragment>
                    ))}
                </div>

                <button
                    className={`px-3 py-1 rounded-md text-sm ${hasNextPage
                        ? 'bg-secondary-500 text-white hover:bg-secondary-600'
                        : 'bg-accent-sand text-text-description cursor-not-allowed'
                        }`}
                    onClick={() => hasNextPage && onPageChange(currentPage + 1)}
                    disabled={!hasNextPage}
                >
                    Next
                </button>
            </div>

            {/* Total items counter */}
            <div className="text-sm text-text-description">
                Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} items
            </div>
        </div>
    );
};

export default Pagination;
